<template>
  <DataTable
    :value="list"
    paginator
    :rows="5"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    tableStyle="min-width: 50rem"
    :loading="loadingRequisites"
  >
    <Column field="index" header="#" style="width: 3%">
      <template #body="{ index }">
        {{ index + 1 }}
      </template>
    </Column>
    <Column field="codigo_rude" header="RUDE" style="width: 15%"></Column>
    <Column field="paterno" header="PATERNO" style="width: 12%"></Column>
    <Column field="materno" header="MATERNO" style="width: 12%"></Column>
    <Column field="nombre" header="NOMBRE" style="width: 15%"></Column>
    <Column
      field="carnet_identidad"
      header="CARNET"
      style="width: 10%"
    ></Column>
    <Column
      field="fecha_nacimiento"
      header="FECHA NACIMIENTO"
      style="width: 12%"
    >
      <template #body="{ data }">
        {{ formatDate(data.fecha_nacimiento) }}
      </template>
    </Column>
    <Column
      field="area"
      header="ÁREA DE PARTICIPACIÓN"
      style="width: 15%"
    ></Column>
    <Column header="ACCIONES" style="width: 10%">
      <template #body="{ data }">
        <div class="flex gap-2 align-items-center">
          <Button
            severity="info"
            @click="showRequisiteOptions(data.codigo_rude)"
            class="requisite-button p-1"
            rounded
            text
            aria-label="Ver requisitos"
            :disabled="
              loadingRequisites && currentCompetitorId === data.codigo_rude
            "
          >
            <i class="pi pi-search"></i>
            <ProgressSpinner
              v-if="
                loadingRequisites && currentCompetitorId === data.codigo_rude
              "
              style="width: 20px; height: 20px"
              strokeWidth="6"
              animationDuration=".5s"
            />
          </Button>
        </div>
      </template>
    </Column>
  </DataTable>

  <!-- Requisites Selection Dialog -->
  <Dialog
    v-model:visible="requisitesDialogVisible"
    header="Requisitos del participante"
    :style="{ width: '50vw', maxWidth: '600px' }"
    :modal="true"
    :closable="!loadingRequisites"
  >
    <div v-if="loadingRequisites" class="flex justify-content-center py-4">
      <ProgressSpinner />
    </div>
    <div v-else class="requisites-list">
      <div
        v-for="(item, index) in requisiteItems[0]?.items"
        :key="index"
        class="requisite-item p-3 border-round cursor-pointer flex align-items-center gap-3"
        :class="{ 'requisite-item-disabled': item.disabled }"
        @click="!item.disabled && showFilesDialog(item)"
      >
        <i :class="item.icon" class="requisite-icon"></i>
        <span class="requisite-label">{{ item.label }}</span>
        <i v-if="item.disabled" class="pi pi-lock ml-auto"></i>
      </div>
      <div
        v-if="!requisiteItems[0]?.items?.length"
        class="text-center py-4 no-requisites"
      >
        No hay requisitos disponibles para este participante
      </div>
    </div>
    <template #footer>
      <Button
        label="Cerrar"
        icon="pi pi-times"
        @click="requisitesDialogVisible = false"
        severity="secondary"
        :disabled="loadingRequisites"
      />
    </template>
  </Dialog>

  <!-- Files Dialog (existing) -->
  <Dialog
    v-model:visible="fileDialogVisible"
    :header="
      selectedRequisite?.requisite_catalog?.description || 'Documentos Adjuntos'
    "
    :style="{ width: '50vw', maxWidth: '800px' }"
    :modal="true"
    :closable="!filesLoading"
  >
    <!-- ... keep your existing files dialog content ... -->
  </Dialog>
</template>

<script setup>
import { ref, computed } from "vue";
import axios from "@/includes/connection";
import Swal from "sweetalert2";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import ProgressSpinner from "primevue/progressspinner";
import Dialog from "primevue/dialog";

const props = defineProps({
  students: Array,
  statusEnrollment: Boolean,
  participantSelected: Array,
});

const emit = defineEmits(["toggleParticipant", "student-deleted"]);

// Refs
const requisitesDialogVisible = ref(false);
const fileDialogVisible = ref(false);
const selectedRequisite = ref(null);
const files = ref([]);
const filesLoading = ref(false);
const downloadingFile = ref(null);
const loadingRequisites = ref(false);
const currentCompetitorId = ref(null);
const requisiteItems = ref([]);
const listRequires = ref([]);

// Computed
const list = computed(() => props.students || []);

// Methods
const showRequisiteOptions = async (competitorId) => {
  try {
    loadingRequisites.value = true;
    currentCompetitorId.value = competitorId;
    requisiteItems.value = [];
    requisitesDialogVisible.value = true;

    const response = await axios.post("/institution/get-info-by-competitor", {
      competitorId: competitorId,
    });

    listRequires.value = response.data;
    const listItems = listRequires.value.competitorRequisites.map((item) => ({
      label: item.requisite_catalog?.description || "Sin descripción",
      icon: "pi pi-file",
      disabled: item.filesCount === 0,
      ...item, // include all original item properties
    }));

    requisiteItems.value = [{ items: listItems }];
  } catch (error) {
    console.error("Error loading requisites:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo cargar la información de requisitos",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
    });
    openFallbackDialog();
  } finally {
    loadingRequisites.value = false;
  }
};

const showFilesDialog = async (requisite) => {
  console.log("Selected requisite:", requisite);
  try {
    selectedRequisite.value = requisite;
    filesLoading.value = true;
    fileDialogVisible.value = true;
    files.value = [];
    requisitesDialogVisible.value = false;

    const response = await axios.post("/institution/send-resource-loaded", {
      params: {
        competitorId: currentCompetitorId.value,
        requisiteId: requisite.id,
      },
    });

    files.value = response.data;
  } catch (error) {
    console.error("Error loading files:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "Error al cargar los archivos",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
    });
  } finally {
    filesLoading.value = false;
  }
};

const openFallbackDialog = () => {
  fileDialogVisible.value = true;
  selectedRequisite.value = {
    requisite_catalog: { description: "Todos los requisitos" },
    id: currentCompetitorId.value,
  };
  loadAllFiles();
};

const loadAllFiles = async () => {
  try {
    filesLoading.value = true;
    const response = await axios.get("/institution/get-files", {
      params: {
        competitorId: currentCompetitorId.value,
      },
    });
    files.value = response.data;
  } catch (error) {
    console.error("Error loading all files:", error);
    files.value = [];
  } finally {
    filesLoading.value = false;
  }
};

const downloadFile = async (fileId) => {
  console.log("Downloading file with ID:", fileId);
  try {
    downloadingFile.value = fileId;
    const response = await axios({
      url: `/institution/download-file/${fileId}`,
      method: "GET",
      responseType: "blob",
    });

    const contentDisposition = response.headers["content-disposition"];
    const fileNameMatch = contentDisposition?.match(/filename="?(.+)"?/);
    const fileName = fileNameMatch?.[1] || `documento_${fileId}`;

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

    Swal.fire({
      icon: "success",
      title: "Descarga iniciada",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
    });
  } catch (error) {
    console.error("Error downloading file:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo descargar el archivo",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
    });
  } finally {
    downloadingFile.value = null;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    return date.toLocaleDateString("es-BO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

const confirmDelete = (studentId) => {
  Swal.fire({
    title: "¿Estás seguro?",
    text: "¡Esta acción no se puede deshacer!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    focusCancel: true,
    reverseButtons: true,
  }).then((result) => {
    if (result.isConfirmed) {
      deleteStudent(studentId);
    }
  });
};

const deleteStudent = async (id) => {
  try {
    await axios.post("/institution/removed-student", { studentId: id });
    emit("student-deleted", id);
    Swal.fire({
      icon: "success",
      title: "¡Eliminado!",
      text: "El estudiante ha sido eliminado",
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (error) {
    console.error("Error deleting student:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo eliminar el estudiante",
      timer: 2000,
      showConfirmButton: false,
    });
  }
};
</script>

<style scoped>
.flex {
  display: flex;
}

.gap-2 {
  gap: 0.5rem;
}

.align-items-center {
  align-items: center;
}

.justify-content-center {
  justify-content: center;
}

.py-4 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.text-center {
  text-align: center;
}

/* Requisites List Styles */
.requisites-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.requisite-item {
  transition: all 0.2s ease;
  border: 1px solid #dee2e6;
}

.requisite-item:hover:not(.requisite-item-disabled) {
  background-color: #f8f9fa;
  border-color: #ced4da;
}

.requisite-item-disabled {
  color: #6c757d;
  cursor: not-allowed;
}

.requisite-icon {
  color: var(--primary-color);
}

.requisite-label {
  flex-grow: 1;
}

.no-requisites {
  color: #6c757d;
}

/* Button Styles */
.requisite-button {
  position: relative;
  min-width: 2rem;
}

.p-button.p-button-text:not(:disabled):hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* Table Styles */
.truncate-text {
  display: inline-block;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
</style>
